import { AskCancelOutput, ConfirmCancelInput } from '@allocab/allocab-passenger-shared-types';
import { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';

import { SecondaryButton } from '../components/button/SecondaryButton';
import { ErrorPage } from '../components/error-page/ErrorPage';
import { Spinner } from '../components/Spinner';
import { CancelTripModal } from '../components/trip/cancel-trip-modal/CancelTripModal';
import { DiscoverAllocab } from '../components/trip/DiscoverAllocab';
import { TripDetail } from '../components/trip/TripDetail';
import { TripHelp } from '../components/trip/TripHelp';
import { TripOptions } from '../components/trip/TripOptions';
import { UpdateTripButtonAnonymous } from '../components/trip/update-trip/UpdateTripButtonAnonymous';
import { TripMap } from '../components/TripMap';
import { constants } from '../constants';
import { useAskAnonymousCancellation } from '../hooks/useAskAnonymousCancellation';
import { useConfirmAnonymousCancellation } from '../hooks/useConfirmAnonymousCancellation';
import useCreateAnonymousNote from '../hooks/useCreateAnonymousNote';
import { useFetchAnonymousTrip } from '../hooks/useFetchAnonymousTrip';
import { useFetchAnonymousTripGuideDownload } from '../hooks/useFetchAnonymousTripGuideDownload';
import { t } from '../services/translation/translation';
import { TripExtraProvider } from '../services/trip-extra/TripExtraProvider';
import { getRefreshInterval, getStepForTrip } from '../utils/trip';

export const ShareRoute = () => {
  const searchingDriverRef = useRef(null);
  const [cancelRequest, setCancelRequest] = useState<AskCancelOutput | null>(null);
  const { jwtToken } = useParams();
  invariant(jwtToken, 'Expected jwtToken');
  const { data: trip, isLoading } = useFetchAnonymousTrip(jwtToken, {
    staleTime: 1000,
    refetchInterval: getRefreshInterval,
    refetchOnWindowFocus: true,
  });
  const askAnonymousCancellationMutation = useAskAnonymousCancellation(jwtToken);
  const confirmAnonymousCancellationMutation = useConfirmAnonymousCancellation(jwtToken);
  const createAnonymousNoteMutation = useCreateAnonymousNote(jwtToken);

  const { data: dataGuide } = useFetchAnonymousTripGuideDownload(jwtToken, trip?.key ? trip.key : '');

  const handleAskCancellation = async () => {
    askAnonymousCancellationMutation.mutate(undefined, {
      onSuccess: data => setCancelRequest(data),
    });
  };

  const handleConfirmCancellation = async (input: ConfirmCancelInput) => {
    confirmAnonymousCancellationMutation.mutate(input, { onSuccess: () => setCancelRequest(null) });
  };

  if (isLoading) {
    return (
      <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center">
        <Spinner className="scale-[3]" />
      </div>
    );
  }

  if (!trip) {
    return <ErrorPage />;
  }

  const step = getStepForTrip(trip);

  return (
    <>
      <Helmet>
        <title>{t('share-route.meta-title', { tripId: trip.id })}</title>
      </Helmet>
      <div className="flex flex-col xl:flex-row h-[calc(100vh_-_4rem)]">
        <div className="w-full xl:w-[608px] order-2 xl:order-1 xl:h-full xl:overflow-y-auto flex flex-col gap-y-2 p-4">
          <TripDetail
            jwtToken={jwtToken}
            searchingDriverRef={searchingDriverRef}
            trip={trip}
            onCreateNote={createAnonymousNoteMutation.mutate}
            isCreatingNote={createAnonymousNoteMutation.isLoading}
            dataGuide={dataGuide}
          />
          {!constants.isHotels &&
          (step === 'BOOKING_CONFIRMED' ||
            step === 'SEARCHING_DRIVER' ||
            step === 'DRIVER_APPROACHING' ||
            step === 'DRIVER_IN_PLACE') ? (
            <TripOptions trip={trip} />
          ) : null}
          <div>
            <TripHelp anonymous={true} trip={trip} />
          </div>
          {trip.state !== 'CANCELLED' && trip.state !== 'DONE' ? (
            <UpdateTripButtonAnonymous trip={trip} jwtToken={jwtToken} />
          ) : null}
          {trip.state !== 'CANCELLED' &&
          trip.state !== 'DONE' &&
          trip.state !== 'ONDUTY' &&
          // "Booking" account
          trip.account.key !== 'Passengeraccount_10910906688' ? (
            <>
              <SecondaryButton
                isLoading={askAnonymousCancellationMutation.isLoading}
                className="w-full mt-2"
                onClick={handleAskCancellation}
              >
                {t('common.cancel')}
              </SecondaryButton>
            </>
          ) : null}
          <DiscoverAllocab />
        </div>
        <div className="w-full order-1 xl:order-2 min-h-[50%] xl:h-full relative">
          <div className="h-full relative" ref={searchingDriverRef}>
            {trip ? (
              <TripExtraProvider>
                <TripMap trip={trip} />
              </TripExtraProvider>
            ) : null}
          </div>
        </div>
      </div>
      {cancelRequest ? (
        <CancelTripModal
          trip={trip}
          cancelRequest={cancelRequest}
          onClose={() => setCancelRequest(null)}
          onConfirm={handleConfirmCancellation}
          isLoading={confirmAnonymousCancellationMutation.isLoading}
        />
      ) : null}
    </>
  );
};
